import { useState } from 'react';
import {
  DEFAULT_WEB_FORM_FIELDS,
  WEB_FORM_FIELD_DEFAULT_LABELS,
  type CreateWebFormInput,
  type UpdateWebFormInput,
  type WebFormFieldConfig,
  type WebFormFieldKey,
  type WebFormResponse,
} from '@orion/contracts';
import { Button, Card, Pill } from '@orion/ui';
import { PageHeader } from '../../../app/shell.js';
import { useLeadSourcesQuery } from '../lead-config/api.js';
import { useCreateWebForm, useDeleteWebForm, useRegenerateWebFormKey, useUpdateWebForm, useWebFormsQuery } from './api.js';

const ALL_FIELD_KEYS: WebFormFieldKey[] = ['email', 'phone', 'companyName', 'jobTitle', 'message'];

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard permission denied — the value is still selectable text.
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-text-muted">{label}</span>
      <div className="flex items-stretch gap-2">
        <code className="flex-1 overflow-x-auto whitespace-pre rounded-control border border-border-strong bg-surface-subtle px-3 py-2 text-xs">
          {value}
        </code>
        <Button type="button" size="sm" variant="secondary" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

function FormPreview({ name, fields }: { name: string; fields: WebFormFieldConfig[] }) {
  return (
    <div className="rounded-card border border-border bg-ground p-5">
      <div className="rounded-card border border-border bg-surface p-5 shadow-card">
        <h3 className="text-base font-semibold text-ink">{name || 'Untitled form'}</h3>
        <p className="mb-4 mt-1 text-xs text-text-muted">Fields marked required must be filled in before you can submit.</p>
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-[13px] font-semibold text-ink">First name</span>
              <div className="h-9 rounded-control border border-border-strong bg-surface" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[13px] font-semibold text-ink">Last name</span>
              <div className="h-9 rounded-control border border-border-strong bg-surface" />
            </div>
          </div>
          {fields.map((f) => (
            <div key={f.key} className="flex flex-col gap-1">
              <span className="text-[13px] font-semibold text-ink">
                {f.label}
                {f.required && <span className="text-danger"> *</span>}
              </span>
              <div className={f.key === 'message' ? 'h-16 rounded-control border border-border-strong bg-surface' : 'h-9 rounded-control border border-border-strong bg-surface'} />
            </div>
          ))}
          <div className="mt-1 h-10 rounded-control bg-primary" />
        </div>
      </div>
    </div>
  );
}

interface DraftState {
  name: string;
  fields: WebFormFieldConfig[];
  successHeadline: string;
  successMessage: string;
  redirectUrl: string;
  sourceId: string;
}

function toDraft(f?: WebFormResponse): DraftState {
  return {
    name: f?.name ?? '',
    fields: f?.fields ?? DEFAULT_WEB_FORM_FIELDS,
    successHeadline: f?.successHeadline ?? "Thanks — we've got it",
    successMessage: f?.successMessage ?? 'Someone from our team will be in touch shortly.',
    redirectUrl: f?.redirectUrl ?? '',
    sourceId: f?.sourceId ?? '',
  };
}

function WebFormEditor({
  form,
  onClose,
  onCreated,
}: {
  form?: WebFormResponse;
  onClose: () => void;
  onCreated?: (form: WebFormResponse) => void;
}) {
  const { data: sources } = useLeadSourcesQuery();
  const createForm = useCreateWebForm();
  const updateForm = useUpdateWebForm();
  const regenerateKey = useRegenerateWebFormKey();
  const deleteForm = useDeleteWebForm();
  const [draft, setDraft] = useState<DraftState>(toDraft(form));
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const savedId = form?.id ?? null;

  const included = new Set(draft.fields.map((f) => f.key));

  const toggleField = (key: WebFormFieldKey) => {
    if (included.has(key)) {
      setDraft((d) => ({ ...d, fields: d.fields.filter((f) => f.key !== key) }));
    } else {
      setDraft((d) => ({ ...d, fields: [...d.fields, { key, label: WEB_FORM_FIELD_DEFAULT_LABELS[key], required: false }] }));
    }
  };

  const updateFieldAt = (index: number, patch: Partial<WebFormFieldConfig>) => {
    setDraft((d) => ({ ...d, fields: d.fields.map((f, i) => (i === index ? { ...f, ...patch } : f)) }));
  };

  const moveField = (index: number, dir: -1 | 1) => {
    setDraft((d) => {
      const next = [...d.fields];
      const swapWith = index + dir;
      const a = next[index];
      const b = next[swapWith];
      if (!a || !b) return d;
      next[index] = b;
      next[swapWith] = a;
      return { ...d, fields: next };
    });
  };

  const save = async () => {
    setError(null);
    const name = draft.name.trim();
    const fields = draft.fields;
    const successHeadline = draft.successHeadline.trim();
    const successMessage = draft.successMessage.trim();
    const redirectUrl = draft.redirectUrl.trim();
    const sourceId = draft.sourceId;

    try {
      if (savedId) {
        const payload: UpdateWebFormInput = {
          name,
          fields,
          successHeadline,
          successMessage,
          redirectUrl: redirectUrl || null,
          sourceId: sourceId || null,
        };
        await updateForm.mutateAsync({ id: savedId, input: payload });
      } else {
        const payload: CreateWebFormInput = {
          name,
          fields,
          successHeadline,
          successMessage,
          redirectUrl: redirectUrl || undefined,
          sourceId: sourceId || undefined,
        };
        const created = await createForm.mutateAsync(payload);
        onCreated?.(created);
      }
    } catch {
      setError('Some values were invalid — check the fields below (a form needs a name and at least one field).');
    }
  };

  const saved = savedId !== null;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const isPending = createForm.isPending || updateForm.isPending;

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-[15px] font-semibold text-ink">{saved ? 'Edit form' : 'Create a new form'}</h3>
        <button type="button" onClick={onClose} className="text-xs font-medium text-text-muted hover:text-ink">
          Close
        </button>
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="grid grid-cols-[1.2fr_1fr] gap-6">
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Form name
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="e.g. Contact us, Request a demo"
              className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal"
            />
          </label>

          <div>
            <span className="mb-2 block text-[13px] font-semibold text-ink">Fields</span>
            <p className="mb-2 text-xs text-text-muted">First and last name are always collected. Add more below, then reorder and mark any as required.</p>
            <div className="flex flex-wrap gap-2">
              {ALL_FIELD_KEYS.map((key) => (
                <label
                  key={key}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                    included.has(key) ? 'border-primary bg-primary-soft text-primary-soft-text' : 'border-border-strong text-text-secondary'
                  }`}
                >
                  <input type="checkbox" className="sr-only" checked={included.has(key)} onChange={() => toggleField(key)} />
                  {WEB_FORM_FIELD_DEFAULT_LABELS[key]}
                </label>
              ))}
            </div>

            {draft.fields.length > 0 && (
              <div className="mt-3 flex flex-col gap-2">
                {draft.fields.map((f, i) => (
                  <div key={f.key} className="flex items-center gap-2 rounded-control border border-border p-2">
                    <div className="flex flex-col">
                      <button type="button" disabled={i === 0} onClick={() => moveField(i, -1)} className="text-text-muted disabled:opacity-30">
                        ▲
                      </button>
                      <button type="button" disabled={i === draft.fields.length - 1} onClick={() => moveField(i, 1)} className="text-text-muted disabled:opacity-30">
                        ▼
                      </button>
                    </div>
                    <input
                      type="text"
                      value={f.label}
                      onChange={(e) => updateFieldAt(i, { label: e.target.value })}
                      className="h-8 flex-1 rounded-control border border-border-strong bg-surface px-2 text-sm"
                    />
                    <label className="flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-text-secondary">
                      <input type="checkbox" checked={f.required} onChange={(e) => updateFieldAt(i, { required: e.target.checked })} />
                      Required
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Lead source attribution
            <select
              value={draft.sourceId}
              onChange={(e) => setDraft((d) => ({ ...d, sourceId: e.target.value }))}
              className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal"
            >
              <option value="">Default ("Website form")</option>
              {(sources ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Success headline
            <input
              type="text"
              value={draft.successHeadline}
              onChange={(e) => setDraft((d) => ({ ...d, successHeadline: e.target.value }))}
              className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal"
            />
          </label>
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Success message
            <input
              type="text"
              value={draft.successMessage}
              onChange={(e) => setDraft((d) => ({ ...d, successMessage: e.target.value }))}
              className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal"
            />
          </label>
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Redirect URL <span className="font-normal text-text-muted">(optional — sends visitors here after submitting)</span>
            <input
              type="text"
              value={draft.redirectUrl}
              onChange={(e) => setDraft((d) => ({ ...d, redirectUrl: e.target.value }))}
              placeholder="https://oes.com/thank-you"
              className="h-9 rounded-control border border-border-strong bg-surface px-3 text-sm font-normal"
            />
          </label>

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} loading={isPending} disabled={!draft.name.trim() || draft.fields.length === 0}>
              {saved ? 'Save changes' : 'Create form'}
            </Button>
            {saved && (
              <button
                type="button"
                className="text-xs font-semibold text-danger"
                onClick={() => setConfirmDelete(true)}
              >
                Delete form
              </button>
            )}
          </div>

          {confirmDelete && (
            <div className="flex items-center gap-2 rounded-control bg-danger-soft px-3 py-2 text-sm">
              <span className="flex-1 text-danger">Delete this form? Existing leads it captured stay in the CRM.</span>
              <button
                type="button"
                className="text-xs font-semibold text-danger"
                onClick={async () => {
                  await deleteForm.mutateAsync(savedId!);
                  onClose();
                }}
              >
                Confirm
              </button>
              <button type="button" className="text-xs font-semibold text-text-muted" onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <FormPreview name={draft.name} fields={draft.fields} />

          {saved && form?.publicKey && (
            <div className="flex flex-col gap-3 rounded-card border border-border p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-text-muted">Embed this form</h4>
              <CopyField label="Direct link" value={`${origin}/forms/${form.publicKey}`} />
              <CopyField
                label="Embed on your website"
                value={`<iframe src="${origin}/embed/forms/${form.publicKey}" style="width:100%;max-width:480px;height:640px;border:0;" title="${draft.name || 'Contact form'}"></iframe>`}
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-muted">{form.submissionCount} submission{form.submissionCount === 1 ? '' : 's'} so far</span>
                <button
                  type="button"
                  className="text-xs font-semibold text-primary"
                  onClick={() => regenerateKey.mutate(savedId!)}
                  disabled={regenerateKey.isPending}
                >
                  Regenerate link
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Admin ▸ Web forms — the full forms builder (docs section 9 "Lead
 * Capture"): create as many distinct public forms as needed, each with its
 * own field set, success copy, lead-source attribution and embeddable
 * public key, superseding the old one-form-per-API-key setup (Admin ▸ API
 * keys' `/api/v1/public/leads` stays for headless/API integrations). */
export function WebFormsPage() {
  const { data, isLoading } = useWebFormsQuery();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const forms = data ?? [];
  const editingForm = editingId ? forms.find((f) => f.id === editingId) : undefined;

  return (
    <>
      <PageHeader
        title="Web forms"
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            Create form
          </Button>
        }
      />
      <main className="flex flex-1 flex-col gap-4 p-7">
        {creating && (
          <WebFormEditor
            key="new"
            onClose={() => setCreating(false)}
            onCreated={(created) => {
              setCreating(false);
              setEditingId(created.id);
            }}
          />
        )}
        {editingForm && <WebFormEditor key={editingForm.id} form={editingForm} onClose={() => setEditingId(null)} />}

        {isLoading && <p className="text-sm text-text-muted">Loading…</p>}

        {!isLoading && forms.length === 0 && !creating && (
          <Card className="p-8 text-center">
            <h3 className="text-base font-semibold text-ink">No forms yet</h3>
            <p className="mt-1 text-sm text-text-muted">Create a form to start capturing leads from your website.</p>
            <Button size="sm" className="mt-4" onClick={() => setCreating(true)}>
              Create your first form
            </Button>
          </Card>
        )}

        {forms.length > 0 && (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            {forms.map((f) => (
              <Card key={f.id} className="flex flex-col gap-3 p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-semibold text-ink">{f.name}</h3>
                  <Pill label={f.isActive ? 'active' : 'paused'} color={f.isActive ? '#1E7A4C' : '#5E6470'} />
                </div>
                <p className="text-xs text-text-muted">
                  {f.fields.length + 2} field{f.fields.length === 1 ? '' : 's'} · {f.submissionCount} submission
                  {f.submissionCount === 1 ? '' : 's'}
                  {f.sourceName ? ` · attributed to "${f.sourceName}"` : ''}
                </p>
                <button type="button" className="self-start text-xs font-semibold text-primary" onClick={() => setEditingId(f.id)}>
                  Edit &amp; get embed code
                </button>
              </Card>
            ))}
          </div>
        )}
      </main>
    </>
  );
}
